import { get } from "lodash";
import { useFormContext } from "react-hook-form";
import type { Action, Audience } from "../../generated/WorkflowsApi.types";
import type { TriggerWithRules } from "../../types";
import { useIsAiAutomationsV2Enabled } from "../Actions/AiAgents/renamePostTitle/useIsAiAutomationsV2Enabled";
import { getActionHasValue } from "./getActionHasValue";
import { getAudienceHasValue } from "./getAudienceHasValue";
import { getTriggerHasValue } from "./getTriggerHasValue";
import { hasSomeTruthyValues } from "./hasSomeTruthyValues";
import { hasTruthyValues } from "./hasTruthyValues";

const getHasValue = (
  name: string,
  input: unknown,
  isAiAutomationsV2Enabled: boolean,
): boolean => {
  if (name.startsWith("actions")) {
    return getActionHasValue(input as Action, isAiAutomationsV2Enabled);
  }

  if (name.startsWith("audience")) {
    return getAudienceHasValue(input as Audience);
  }

  if (name.startsWith("trigger")) {
    return getTriggerHasValue(input as TriggerWithRules);
  }

  return hasTruthyValues(input);
};

interface FieldStatus {
  hasError: boolean;
  hasId: boolean;
  hasValue: boolean;
  isDirty: boolean;
  isTouched: boolean;
  value: unknown;
}

interface ValueWithId {
  [key: string]: unknown;
  id?: string | number;
}

export const useFieldStatus = (name: string): FieldStatus => {
  const { getFieldState, formState, watch } = useFormContext();
  const isAiAutomationsV2Enabled = useIsAiAutomationsV2Enabled();

  const value = watch(name);
  const { error: inputError } = getFieldState(name, formState);
  const { dirtyFields, touchedFields } = formState;

  const dirtyInputValue = get(dirtyFields, name, null);
  const isDirty = hasSomeTruthyValues(dirtyInputValue);

  const touchedInputValue = get(touchedFields, name, null);
  const isTouched = hasSomeTruthyValues(touchedInputValue);

  const communitySegmentId = watch("community_segment_id");
  const hasId = name.startsWith("audience")
    ? Boolean(communitySegmentId) || Boolean((value as ValueWithId)?.id)
    : Boolean((value as ValueWithId)?.id);

  const hasError = Boolean(inputError);
  const hasValue = getHasValue(name, value, isAiAutomationsV2Enabled);

  return {
    isDirty,
    isTouched,
    hasError,
    hasValue,
    hasId,
    value,
  };
};
